import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type { FailedMeasure, PivotDataResponse } from "../../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../../contexts/ApiClientProviderContext.ts";
import { createCurrentReportAction, createDevToolsAction } from "../../../../../store/utils/storeUtils";
import { useIsAddingFields } from "../../../common/fields/useFieldDestination.ts";
import useDeferredBuild from "../../../common/hooks/useDeferredBuild.ts";
import useRequestConfigBuilder from "./useRequestConfigBuilder.ts";

type CancellablePivotRequest = {
  cancel: () => void;
};

export default function useDataLoadingBuilder() {
  const isAdding = useIsAddingFields();
  const dispatch = useDispatch();
  const [cancellationToken, setCancellationToken] = React.useState<CancellablePivotRequest | undefined>(undefined);
  const { requestConfig } = useRequestConfigBuilder();
  const { buildPivotReport } = useEnhancedBiApiClientProvider();
  const [calculateCount, setCalculateCount] = React.useState(0);
  const [pivot, setPivot] = React.useState<PivotDataResponse>();
  const [error, setError] = React.useState<string>();
  const [failedMeasures, setFailedMeasures] = React.useState<FailedMeasure[]>([]);

  const calculating = calculateCount > 0;

  const calculatingRef = React.useRef<number>(calculateCount);
  const cancellationTokenRef = React.useRef<CancellablePivotRequest | undefined>(cancellationToken);
  React.useEffect(() => {
    calculatingRef.current = calculateCount;
  }, [calculateCount]);
  React.useEffect(() => {
    cancellationTokenRef.current = cancellationToken;
  }, [cancellationToken]);

  const buildConfigRef = React.useRef(requestConfig);
  React.useEffect(() => {
    buildConfigRef.current = requestConfig;
  }, [requestConfig]);

  React.useEffect(() => {
    return () => {
      if (cancellationTokenRef.current !== undefined) {
        cancellationTokenRef.current.cancel();
      }
    };
  }, []);

  const handleBuildDone = (response: ApiResponse<PivotDataResponse>) => {
    if (response.success) {
      if (response.data.errorCode) {
        setError(response.data.errorCode);
      } else {
        setError(undefined);
        setPivot(response.data);
        dispatch(createDevToolsAction("update", { loggingItems: response.data.loggingItems }));
        dispatch(createCurrentReportAction("setEtag", { etag: response.data.etag, cacheValid: undefined }));
        setFailedMeasures(response.data.failedMeasures);
      }
    } else if (response.error?.message) {
      setError(response.error.message);
    }
    setCalculateCount(calculatingRef.current - 1);
  };

  const handleBuildError = (reason: string | undefined) => {
    if (reason === "canceled") {
      setError(undefined);
    } else {
      setError(reason || "An error occurred while calculating report data. Please try again.");
    }
    setCalculateCount(calculatingRef.current - 1);
  };

  const calculate = (
    onBuildDone: (response: ApiResponse<PivotDataResponse>) => void,
    onBuildError: (reason: string | undefined) => void
  ) => {
    setCalculateCount(calculatingRef.current + 1);
    setError(undefined);
    dispatch(createCurrentReportAction("setEtag", { etag: undefined, cacheValid: undefined }));

    setCancellationToken(buildPivotReport?.(buildConfigRef.current, onBuildDone, onBuildError));
  };

  const { doRequest, resetInitialRequestFlag, cancelCurrentAwaiter } = useDeferredBuild({
    paused: isAdding,
    request: calculate,
    handleBuildDone,
    handleBuildError,
    cancelRequest: () => cancellationTokenRef.current?.cancel(),
    onProgressChange: (progress) => dispatch(createCurrentReportAction("updateBuildProgress", progress)),
  });

  const handleInvalidConfiguration = () => {
    cancelCurrentAwaiter();
    cancellationTokenRef.current?.cancel();
    setPivot(undefined);
    setError(undefined);
    setFailedMeasures([]);
  };

  useEffect(() => {
    resetInitialRequestFlag();
  }, [requestConfig.reportId, resetInitialRequestFlag]);

  return { pivot, calculating, error, calculate: doRequest, failedMeasures, handleInvalidConfiguration };
}
